import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArchiveRestore,
  ChevronLeft,
  ChevronRight,
  LoaderCircle,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import type { DeletedDocument, DeletedDocumentList } from '../shared/desktop-api';
import { LazyPageThumbnail } from './LazyPageThumbnail';
import { OperatorErrorText } from './TechnicalDetails';
import { WorkspaceDialog } from './WorkspaceDialog';
import { AUTOMATIC_DELETE_SENTENCE } from './document-deletion';
import './corrections-panel.css';
import './deleted-documents.css';

const PAGE_SIZE = 40;

/** A type name worth showing: none for a document that had no type. */
const shownType = (name: string) =>
  name.trim() && !/^unclassified/i.test(name.trim()) ? name.trim() : null;

/**
 * A job's Deleted view (ADR-0096): every document deleted on Manual, and every one a learned junk shape deleted on
 * its own, newest first, each with Restore. Opened from the documents list's More menu, where the job's documents
 * are, or from the empty workspace once none is left; laid out as Review history is (one summary line, then the list), each row the document's first page and
 * name. Restore puts the document back where it was; `onRestored` lets the workspace read its documents again. A
 * Restore the engine refuses because the document is back already (restored from Activity or another window) calls
 * `onAlreadyRestored` instead, so nothing says it was restored here and nothing is read again.
 */
export function DeletedDocumentsDialog({
  jobId,
  onClose,
  onRestored,
  onAlreadyRestored,
}: {
  jobId: string;
  onClose: () => void;
  onRestored: (document: DeletedDocument) => void;
  onAlreadyRestored: (document: DeletedDocument) => void;
}) {
  const [list, setList] = useState<DeletedDocumentList | null>(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState('');
  const [restoring, setRestoring] = useState<string | null>(null);
  const [restoreError, setRestoreError] = useState<{ documentId: string; message: string } | null>(
    null,
  );
  const request = useRef(0);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  // Restore is disabled while it runs, so keyboard focus fell to the page behind the dialog. Once Restore is usable
  // again, whatever the outcome, focus goes to the Restore at the row's place: its own after a failed one, the row now
  // in its place after one restored (the last row's when it was last, on the page shown before when it was a later
  // page's only row), or the dialog itself when nothing is left.
  const root = useRef<HTMLDivElement>(null);
  const refocusAt = useRef<number | null>(null);
  useEffect(() => {
    const at = refocusAt.current;
    if (at === null || !list || restoring !== null) return;
    refocusAt.current = null;
    const restores = [
      ...(root.current?.querySelectorAll<HTMLButtonElement>('.deleted-document > button') ?? []),
    ];
    (
      restores[Math.min(at, restores.length - 1)] ??
      root.current?.closest<HTMLElement>('[role="dialog"]')
    )?.focus();
  }, [list, restoring]);
  const load = useCallback(
    async (at: number): Promise<void> => {
      const api = window.capturePointDesktop;
      const asked = ++request.current;
      setError('');
      try {
        if (!api?.listDeletedDocuments)
          throw new Error('Deleted documents are not available in this build.');
        const page = await api.listDeletedDocuments({ jobId, offset: at, limit: PAGE_SIZE });
        if (!live.current || asked !== request.current) return;
        // Restoring the last document of a later page leaves it empty: show the page before it instead (awaited, so a
        // Restore's refocus waits for that page).
        if (page.documents.length === 0 && at > 0 && page.total > 0)
          return load(Math.max(0, at - PAGE_SIZE));
        setList(page);
        setOffset(page.offset);
      } catch (reason) {
        if (live.current && asked === request.current)
          setError(reason instanceof Error ? reason.message : String(reason));
      }
    },
    [jobId],
  );
  useEffect(() => {
    void load(0);
  }, [load]);

  const restore = async (document: DeletedDocument) => {
    const api = window.capturePointDesktop;
    if (!api?.restoreDocuments || restoring) return;
    const at = documents.findIndex(
      (item) => item.batchId === document.batchId && item.documentId === document.documentId,
    );
    setRestoring(document.documentId);
    setRestoreError(null);
    try {
      const result = await api.restoreDocuments({
        jobId,
        batchId: document.batchId,
        documentIds: [document.documentId],
      });
      if (!live.current) return;
      if (result.done.includes(document.documentId)) onRestored(document);
      else onAlreadyRestored(document);
      await load(offset);
    } catch (reason) {
      if (live.current)
        setRestoreError({
          documentId: document.documentId,
          message: reason instanceof Error ? reason.message : String(reason),
        });
    } finally {
      // Set with the Restore made usable again, so focus comes back for every outcome, after the list is read again.
      if (live.current) {
        refocusAt.current = at;
        setRestoring(null);
      }
    }
  };

  const documents = list?.documents ?? [];
  const total = list?.total ?? 0;
  return (
    <WorkspaceDialog telemetry="deleted" title="Deleted documents" onClose={onClose}>
      <div ref={root} className="correction-history deleted-documents" aria-busy={!list && !error}>
        <div className="history-summary">
          <Trash2 size={18} aria-hidden="true" />
          <span role="status">
            {error && !list
              ? 'Could not load deleted documents'
              : !list
                ? 'Loading deleted documents…'
                : `${total.toLocaleString()} deleted ${total === 1 ? 'document' : 'documents'}`}
          </span>
          {total > 0 && <small>Newest first</small>}
        </div>
        <div className="history-list">
          {error ? (
            <div
              className="history-empty deleted-documents-error"
              role="alert"
              data-telemetry="error:deleted-load-failed"
            >
              <TriangleAlert size={28} aria-hidden="true" />
              <p>Deleted documents couldn’t be loaded.</p>
              <OperatorErrorText message={error} />
              <button type="button" className="quiet-button" onClick={() => void load(offset)}>
                Try again
              </button>
            </div>
          ) : !list ? (
            <p className="deleted-documents-loading">
              <LoaderCircle className="spin" size={16} aria-hidden="true" /> Loading…
            </p>
          ) : documents.length === 0 ? (
            <div className="history-empty">
              <Trash2 size={40} aria-hidden="true" />
              <h3>Nothing deleted</h3>
              <p>Deleted documents show here, and Restore puts one back.</p>
            </div>
          ) : (
            <ul className="deleted-document-list">
              {documents.map((document) => {
                const type = shownType(document.documentType);
                const first = document.pages[0];
                const busy = restoring === document.documentId;
                return (
                  <li
                    className="deleted-document"
                    key={`${document.batchId}:${document.documentId}`}
                  >
                    <span className="deleted-document-page">
                      {first ? (
                        <LazyPageThumbnail
                          previewPath={first.previewPath}
                          label={`First page of ${document.sourceName}`}
                        />
                      ) : (
                        <Trash2 size={18} aria-hidden="true" />
                      )}
                    </span>
                    <span className="deleted-document-meta">
                      <strong>{document.sourceName}</strong>
                      <small>
                        {[
                          type,
                          `${document.pageCount.toLocaleString()} ${document.pageCount === 1 ? 'page' : 'pages'}`,
                          `Deleted ${new Date(document.deletedAtUtc).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}`,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </small>
                      {document.automatic && (
                        <small className="deleted-document-reason">
                          {document.reasonText || AUTOMATIC_DELETE_SENTENCE}
                        </small>
                      )}
                      {restoreError?.documentId === document.documentId && (
                        <small
                          className="deleted-document-error"
                          role="alert"
                          data-telemetry="error:document-restore-failed"
                        >
                          <OperatorErrorText message={restoreError.message} />
                        </small>
                      )}
                    </span>
                    <button
                      type="button"
                      className="secondary-button small"
                      data-telemetry="deleted-restore"
                      disabled={restoring !== null}
                      aria-busy={busy || undefined}
                      aria-label={`${busy ? 'Restoring' : 'Restore'} ${document.sourceName}`}
                      onClick={() => void restore(document)}
                    >
                      {busy ? (
                        <LoaderCircle className="spin" size={14} aria-hidden="true" />
                      ) : (
                        <ArchiveRestore size={14} aria-hidden="true" />
                      )}
                      {busy ? 'Restoring…' : 'Restore'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {list && total > PAGE_SIZE && (
            <div className="deleted-documents-pages">
              <button
                type="button"
                className="quiet-button small"
                disabled={offset === 0}
                onClick={() => void load(Math.max(0, offset - PAGE_SIZE))}
              >
                <ChevronLeft size={12} aria-hidden="true" /> Newer
              </button>
              <small>
                {offset + 1}–{Math.min(offset + documents.length, total)} of {total}
              </small>
              <button
                type="button"
                className="quiet-button small"
                disabled={offset + documents.length >= total}
                onClick={() => void load(offset + PAGE_SIZE)}
              >
                Older <ChevronRight size={12} aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </div>
    </WorkspaceDialog>
  );
}
